import React, { useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usersApi } from "../services/users";
import { AuthContext } from "../context/AuthContext";
import PriceSetupModal, { ProfileBits } from "./PriceSetupModal";
import { needsFirstRunTour } from "../lib/firstRunTour";

// First-sign-in sequencing, mounted in RouteGuards.RequireAuth — the one
// place every authenticated talent page flows through, so every way back
// into the app (silent Stripe-return login, plain login, resume links, a
// cold app open weeks later) meets it. Native and web.
//
// One fresh profile read per shell mount decides the whole first-sign-in
// story (founder — the tour is the ONE container, no stacked pop-ups):
//   tour needed  → route into /first-run-tour, which ends with the price
//                  step (only if still on the registration placeholder) and
//                  hands off to Requests, where the DEMO card lives.
//   otherwise    → PriceSetupModal, fed the same profile so it doesn't read
//                  it again (it stays the catch-all for a price that's still
//                  pending — e.g. the app was killed mid-tour).
// A failed read shows nothing extra and lets PriceSetupModal do its own read:
// the flags are server-side, so both simply appear on the next good load.
const FirstRunTourGate: React.FC = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [state, setState] = useState<{ status: "pending" | "tour" | "pass"; profile?: ProfileBits }>({
    status: "pending",
  });

  useEffect(() => {
    let alive = true;
    if (!user?.slug) return;
    setState({ status: "pending" });
    usersApi
      .get(user.slug)
      .then((data) => {
        if (!alive) return;
        // Never inside the Electron Desktop Companion: it's a 420×720 floating
        // window (collapsed to a 51px bubble at rest) that DJs open mid-gig,
        // and the tour's screens point at phone/web surfaces. Not stamped
        // there either, so the phone or web still shows it once.
        const insideElectron = !!(window as any).electronAPI?.isElectron;
        setState(!insideElectron && needsFirstRunTour(data) ? { status: "tour" } : { status: "pass", profile: data });
      })
      .catch(() => {
        if (alive) setState({ status: "pass" });
      });
    return () => {
      alive = false;
    };
  }, [user?.slug]);

  if (state.status === "pending") return null;
  if (state.status === "tour" && location.pathname !== "/first-run-tour") {
    return <Navigate to="/first-run-tour" replace />;
  }
  return <PriceSetupModal prefetched={state.profile} />;
};

export default FirstRunTourGate;
